import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { AlignItemsProps, DisplayProps, SpaceProps, WidthProps } from 'styled-system';
import { alignItems, display, space, width } from 'styled-system';

import Alert from 'common/components/Alert';
import { Button } from 'common/components/Button';
import Icon from 'common/components/Icon';
import { Link } from 'common/components/Link';
import Separator from 'common/components/Separator';
import { BodyRegularBR1, HeadlineH6, LinkRegularLR1 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { performOptOutOfHomeClubMutation } from 'common/hooks/home-club/use-form-submit';
import { useLocalization } from 'common/providers/LocaleProvider';
import { focusOutlineCSSWithOffsetAndRadius } from 'common/theme/styles';
import type {
  TextDecorationProps,
  TextUnderlinePositionProps,
} from 'common/theme/system-utilities';
import { textDecoration, textUnderlinePosition } from 'common/theme/system-utilities';
import {
  dispatchHomeClubSectionClickEvent,
  HomeClubSectionEventInteraction,
  HomeClubSectionEventPlacement,
} from 'common/tracking/home-club';
import { Element } from 'common/types/tracking';
import HomeClubLogoSection from '../HomeClubLogoSection';

interface HomeClubMemberDrawerProps {
  homeClubId: string;
}

const Container = styled.div`
  ${space};
`;
const containerStyles = {
  p: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
  pt: ['sp_32', 'sp_36', 'sp_40', 'sp_48'],
};

const StyledLink = styled(Link)<SpaceProps & WidthProps>`
  ${space};
  ${width};
`;
const linkStyles = {
  display: 'block',
  mt: 'sp_16',
  width: 'fit-content',
};

const LinkText = styled(LinkRegularLR1)<
  DisplayProps & TextDecorationProps & TextUnderlinePositionProps
>`
  ${textDecoration};
  ${textUnderlinePosition};
`;

const linkTextStyles: ComponentProps<typeof LinkText> = {
  as: 'span',
  textDecoration: 'underline 1px',
  color: 'sys.neutral.text.default',
  textUnderlinePosition: 'from-font',
};

const LinkButton = styled(Button)<SpaceProps & DisplayProps & AlignItemsProps>`
  ${space};
  ${display};
  ${alignItems};

  &:hover {
    text-decoration: underline;
  }
  ${focusOutlineCSSWithOffsetAndRadius};
`;

const HomeClubMemberDrawer = ({ homeClubId }: HomeClubMemberDrawerProps) => {
  const { t, p } = useLocalization();
  const [errorMessage, setErrorMessage] = useState(null);

  const optOutOfHomeClub = async () => {
    dispatchHomeClubSectionClickEvent({
      element: Element.Link,
      button_text: t('h24_hc_drawer_leave_btn_label'),
      placement: HomeClubSectionEventPlacement.Drawer,
      interaction: HomeClubSectionEventInteraction.LeaveHomeClub,
    });

    try {
      await performOptOutOfHomeClubMutation();
      window.location.href = p.t('h24_my_overview_url') + '?hcOptOutSuccess';
    } catch {
      setErrorMessage(t('h24_something_went_wrong'));
    }
  };

  return (
    <>
      <Container
        {...containerStyles}
        {...(!!errorMessage
          ? {
              pt: 'sp_16',
            }
          : {})}
      >
        {!!errorMessage && (
          <Alert type={MessageType.Error} aria-live="polite">
            {errorMessage}
          </Alert>
        )}

        <HomeClubLogoSection homeClubId={homeClubId} />

        <Separator mb="sp_20" />

        <section aria-labelledby="terms">
          <HeadlineH6 mb="sp_8" as="h2" id="terms">
            {t('h24_hc_drawer_terms_headline')}
          </HeadlineH6>
          <BodyRegularBR1>{t('h24_hc_drawer_terms_subtitle')}</BodyRegularBR1>

          <StyledLink
            href={p(t('h24_hc_modal_disclaimer_conditions_href'))}
            target="_blank"
            rel="noopener noreferrer"
            {...linkStyles}
          >
            <LinkText {...linkTextStyles}>{t('h24_hc_drawer_open_terms')}</LinkText>
          </StyledLink>
          <StyledLink
            href={t('h24_hc_modal_faq_href')}
            target="_blank"
            rel="noopener noreferrer"
            {...linkStyles}
          >
            <LinkText {...linkTextStyles}>{t('h24_hc_drawer_open_faqs')}</LinkText>
          </StyledLink>
        </section>

        <Separator my="sp_20" />

        <section aria-labelledby="leave">
          <HeadlineH6 mb="sp_8" as="h2" id="leave">
            {t('h24_hc_drawer_leave_headline')}
          </HeadlineH6>
          <BodyRegularBR1>{t('h24_hc_drawer_leave_subtitle')}</BodyRegularBR1>

          <LinkButton kind="bare" mt="sp_16" alignItems="center" onClick={optOutOfHomeClub}>
            <Icon name="close" size="24px" />
            <LinkText {...linkTextStyles} ml="sp_4">
              {t('h24_hc_drawer_leave_btn_label')}
            </LinkText>
          </LinkButton>
        </section>
      </Container>
    </>
  );
};

export default HomeClubMemberDrawer;
